import { useEffect } from 'react';
import { Group, ActionIcon, Avatar, Menu, useMantineColorScheme, Tooltip } from '@mantine/core';
import { IconSearch, IconSettings, IconUser, IconLogout, IconMoon, IconSun } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '@/store';
import { translate, showLogin } from '@/lib';
import { useNavigate } from 'react-router';
import { getUserInfo } from '@/models/User';
import { useRaresOptions } from '@/models/Metadata';
import style from './Style.module.scss';
import { IconLogo } from '@/components/IconLogo';
import { Language } from '@/components/Language';
import { isInGame } from '@/lib/game';

const TIER_BG_STYLE_ID = 'tier-bg-dynamic';

export function Header() {
  const { updateState } = useStore();
  const navigate = useNavigate();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  // Get user info from cache
  const userInfo = getUserInfo();

  // Fetch rarity data and generate tier-bg-* CSS classes dynamically
  const { data: rares } = useQuery(useRaresOptions());

  useEffect(() => {
    if (!rares || rares.length === 0) return;

    const css = rares
      .map((rare) => {
        const color = rare.color || '#95a5a6';
        return `.tier-bg-${rare.id}{background:${color}}`;
      })
      .join('\n');

    let styleEl = document.getElementById(TIER_BG_STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = TIER_BG_STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;

    return () => {
      styleEl?.remove();
    };
  }, [rares]);

  const handleSearchClick = () => {
    updateState('searchModalOpen', true);
  };

  const handleProfileClick = () => {
    updateState('profileModalOpen', true);
  };

  const handleSettingsClick = () => {
    // TODO: Navigate to settings page or open settings modal
    console.log('Settings clicked');
  };

  const handleLoginClick = () => {
    showLogin();
  };

  const handleLogoutClick = () => {
    // TODO: Implement logout logic
    console.log('Logout clicked');
    // Example: Clear local storage, redirect to login, etc.
    // localStorage.clear();
    // navigate('/login');
    window.location.href = '/api/app/logout';
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleThemeToggle = () => {
    toggleColorScheme();
  };

  return (
    <header className={style.header}>
      <Group justify="space-between" h="100%" px="md">
        <Group gap="sm" style={{ cursor: 'pointer' }} onClick={handleLogoClick}>
          <div className={style.logo}>
            <IconLogo />
          </div>
          <span className={style.brandName}>
            {translate('ff.brand', 'Free Fire Companion')}
          </span>
        </Group>

        <Group gap="md">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={handleSearchClick}
            aria-label="Search"
          >
            <IconSearch size={20} />
          </ActionIcon>

          <Language />

          {/* Show login button if not logged in, otherwise show user menu */}
          {!userInfo ? (
            <Tooltip label={translate('header.user.login', 'Login')}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                onClick={handleLoginClick}
                aria-label="Login"
              >
                <IconUser size={20} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Avatar
                  src={null}
                  alt={userInfo.name || 'Profile'}
                  radius="xl"
                  style={{ cursor: 'pointer' }}
                  color="orange"
                >
                  {userInfo.name?.charAt(0).toUpperCase()}
                </Avatar>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>{userInfo.name || translate('header.user.account', 'Account')}</Menu.Label>

                <Menu.Item
                  leftSection={<IconUser size={16} />}
                  onClick={handleProfileClick}
                >
                  {translate('ff.nav.profile', 'Profile')}
                </Menu.Item>

                <Menu.Item
                  leftSection={<IconSettings size={16} />}
                  onClick={handleSettingsClick}
                  style={{
                    display: 'none',
                  }}
                >
                  {translate('header.user.settings', 'Settings')}
                </Menu.Item>

                <Menu.Divider />

                <Menu.Label>{translate('header.user.appearance', 'Appearance')}</Menu.Label>

                <Menu.Item
                  leftSection={colorScheme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
                  onClick={handleThemeToggle}
                >
                  {colorScheme === 'dark'
                    ? translate('header.user.colorSchemeLight', 'Light Mode')
                    : translate('header.user.colorSchemeDark', 'Dark Mode')
                  }
                </Menu.Item>
                {!isInGame() && (
                  <>
                    <Menu.Divider />
                    <Menu.Item
                      color="red"
                      leftSection={<IconLogout size={16} />}
                      onClick={handleLogoutClick}
                    >
                      {translate('header.user.logOut', 'Logout')}
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
    </header>
  );
}

export default Header;